import { ReactElement, useState } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { HANDICAP_LABELS } from '../../utils/handicapUtils';
import { usgaPostPreviewLines } from '../../utils/usgaPostPreview';
import { usgaRoundLabel } from '../../utils/usgaScorePostUtils';

import EventScoreUsgaPostForm from './EventScoreUsgaPostForm';
import EventScoreUsgaPostLog from './EventScoreUsgaPostLog';

import type { UsgaScoreType } from '../../types/UsgaPost';
import type { UsgaPostRowAction, UsgaPostRowStatus } from '../../utils/usgaPostStatus';
import type { UsgaPostBuild } from '../../utils/usgaScorePostUtils';
import type { UsgaPostChoices } from './EventScoreUsgaPostForm';

export interface UsgaPostRow {
  scoreId: string;
  name: string;
  ghin: string | undefined;
  build: UsgaPostBuild;
  status: UsgaPostRowStatus;
  action: UsgaPostRowAction;
  /** Bare-X holes whose value the server works out from the USGA's index when posting. */
  xHolesFromUsgaIndex: ReadonlySet<number>;
  /** The USGA's reason for holding the score for review (e.g. possibly a duplicate, or incomplete). */
  reviewMessage: string | null;
  isTotalOnly: boolean;
  defaultAdjustedScore: number;
  defaultHolesPlayed: number;
  /** The score type of the last attempt, for Post again. */
  lastScoreType: UsgaScoreType | null;
  /** What would be sent with these choices. */
  preview: (choices: Omit<UsgaPostChoices, 'scoreType'>) => UsgaPostBuild;
}

interface EventScoreUsgaPostRowProps {
  orgId: string;
  eventId: string;
  row: UsgaPostRow;
  /** Only one row's panel is open at a time across the section. */
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onPost: (choices: UsgaPostChoices) => Promise<void>;
  onConfirm: () => Promise<void>;
}

const ACTION_LABELS: Record<NonNullable<UsgaPostRowAction>, string> = {
  post: 'Post to USGA',
  postAgain: 'Post again',
  confirm: 'Confirm',
};

/** One player's score in the God-only "Post to USGA" section: status, details, and its action. */
export default function EventScoreUsgaPostRow({ orgId, eventId, row, isOpen, onOpen, onClose, onPost, onConfirm }: EventScoreUsgaPostRowProps): ReactElement {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const { name, ghin, build, status, action } = row;

  const handleConfirm = async () => {
    setIsConfirming(true);
    setConfirmError(null);
    try {
      await onConfirm();
      onClose();
    } catch (e) {
      setConfirmError(e instanceof Error ? e.message : 'Couldn\'t send the request.');
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <ButtonBase
          onClick={() => setIsExpanded((v) => !v)}
          aria-expanded={isExpanded}
          sx={{ justifyContent: 'flex-start', textAlign: 'left', borderRadius: 1, flexGrow: 1 }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
            <Typography variant="bodyMedium" sx={{ flexGrow: 1 }}>{name}</Typography>
            {build.ok && (
              <Typography variant="bodySmall" color="text.secondary">{usgaRoundLabel(build.round)}</Typography>
            )}
            <Chip size="small" label={status.label} color={status.color} variant={status.color === 'default' ? 'outlined' : 'filled'} />
          </Stack>
        </ButtonBase>
        {action && !isOpen && (
          <Button size="small" variant="outlined" onClick={onOpen}>{ACTION_LABELS[action]}</Button>
        )}
      </Stack>

      {isExpanded && (
        <Stack spacing={0.5}>
          <Typography variant="bodySmall" color="text.secondary">{HANDICAP_LABELS.ID}: {ghin || '—'}</Typography>
          {status.detail && <Typography variant="bodySmall">{status.detail}</Typography>}
          {build.ok
            ? usgaPostPreviewLines(build.sent, row.xHolesFromUsgaIndex).map((line) => (
              <Typography key={line} variant="bodySmall" color="text.secondary">{line}</Typography>
            ))
            // A blocked row's status detail is already this reason — don't show it twice.
            : build.block.message !== status.detail && (
              <Typography variant="bodySmall" color="text.secondary">{build.block.message}</Typography>
            )}
          <EventScoreUsgaPostLog orgId={orgId} eventId={eventId} scoreId={row.scoreId} />
        </Stack>
      )}

      {isOpen && (action === 'post' || action === 'postAgain') && (
        <EventScoreUsgaPostForm
          isTotalOnly={row.isTotalOnly}
          defaultAdjustedScore={row.defaultAdjustedScore}
          defaultHolesPlayed={row.defaultHolesPlayed}
          initialScoreType={action === 'postAgain' ? row.lastScoreType : null}
          submitLabel={action === 'postAgain' ? 'Post again' : 'Post'}
          preview={row.preview}
          xHolesFromUsgaIndex={row.xHolesFromUsgaIndex}
          onSubmit={async (choices) => {
            await onPost(choices);
            onClose();
          }}
          onCancel={onClose}
        />
      )}

      {isOpen && action === 'confirm' && (
        <Stack spacing={1}>
          <Typography variant="bodySmall">
            {row.reviewMessage ?? 'The USGA is holding this score for review.'} Confirm to post it.
          </Typography>
          {confirmError && <Alert severity="error">{confirmError}</Alert>}
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" disabled={isConfirming} onClick={handleConfirm}>Confirm</Button>
            <Button size="small" onClick={onClose} disabled={isConfirming}>Cancel</Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
